/**
 * <article-actions> — handles the SAVE (localStorage toggle) and SHARE
 * (reveal social icons) buttons on the article header.
 */
if (!customElements.get('article-actions')) {
  customElements.define(
    'article-actions',
    class ArticleActions extends HTMLElement {
      constructor() {
        super();
        this.STORAGE_KEY = '58peaks-saved-articles';
        this.handleSaveClick = this.handleSaveClick.bind(this);
        this.handleShareClick = this.handleShareClick.bind(this);
        this.handleOutsideClick = this.handleOutsideClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
      }

      connectedCallback() {
        this.articleHandle = this.dataset.articleHandle;

        this.saveBtn = this.querySelector('[data-save-toggle]');
        this.shareBtn = this.querySelector('[data-share-toggle]');
        this.shareOptions = this.querySelector('[data-share-options]');

        if (this.saveBtn) {
          this.saveBtn.addEventListener('click', this.handleSaveClick);
          this.reflectSavedState();
        }

        if (this.shareBtn && this.shareOptions) {
          this.shareBtn.addEventListener('click', this.handleShareClick);
          document.addEventListener('click', this.handleOutsideClick);
          document.addEventListener('keydown', this.handleKeydown);
        }
      }

      disconnectedCallback() {
        if (this.saveBtn) this.saveBtn.removeEventListener('click', this.handleSaveClick);
        if (this.shareBtn) this.shareBtn.removeEventListener('click', this.handleShareClick);
        document.removeEventListener('click', this.handleOutsideClick);
        document.removeEventListener('keydown', this.handleKeydown);
      }

      readSaved() {
        try {
          const raw = window.localStorage.getItem(this.STORAGE_KEY);
          return raw ? JSON.parse(raw) : [];
        } catch (_) {
          return [];
        }
      }

      writeSaved(list) {
        try {
          window.localStorage.setItem(this.STORAGE_KEY, JSON.stringify(list));
        } catch (_) {
          // Storage unavailable (private mode); visual state still toggles.
        }
      }

      reflectSavedState() {
        if (!this.articleHandle) return;
        const list = this.readSaved();
        const isSaved = list.includes(this.articleHandle);
        this.saveBtn.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
        this.saveBtn.classList.toggle('is-saved', isSaved);
      }

      handleSaveClick(event) {
        event.preventDefault();
        if (!this.articleHandle) return;

        const list = this.readSaved();
        const idx = list.indexOf(this.articleHandle);
        if (idx === -1) {
          list.push(this.articleHandle);
        } else {
          list.splice(idx, 1);
        }
        this.writeSaved(list);
        this.reflectSavedState();
      }

      handleShareClick(event) {
        event.preventDefault();
        event.stopPropagation();
        const isOpen = this.shareBtn.getAttribute('aria-expanded') === 'true';
        this.setShareOpen(!isOpen);
      }

      handleOutsideClick(event) {
        if (!this.shareOptions || this.shareBtn.getAttribute('aria-expanded') !== 'true') return;
        if (this.contains(event.target)) return;
        this.setShareOpen(false);
      }

      handleKeydown(event) {
        if (event.key === 'Escape' && this.shareBtn && this.shareBtn.getAttribute('aria-expanded') === 'true') {
          this.setShareOpen(false);
          this.shareBtn.focus();
        }
      }

      setShareOpen(open) {
        this.shareBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) {
          this.shareOptions.removeAttribute('hidden');
        } else {
          this.shareOptions.setAttribute('hidden', '');
        }
      }
    }
  );
}
